/*
 * Optimus scores the governed metric Cphere has already established. It does
 * not reconstruct a rate from whole-campaign totals.
 *
 * The canonical layer decides which observations may divide: a Video View Rate
 * counts the views of the platforms that measured a view, over the impressions
 * of those same observations. CTV is excluded because it reports video STARTS,
 * which are not a compatible view; Display is excluded because it reports no
 * view at all. The backend already resolves this (utils/metricBasis, stamped
 * onto every rollup by applyEligibleRatesToRollup) and ships it on the Optimus
 * snapshot as `videoViewRate`.
 *
 * Optimus dropped it on the floor. Every totals projection on the page
 * whitelists the fields it carries forward, and `videoViewRate` /
 * `engagementRate` were never on any of those lists — so the governed rate was
 * fetched, discarded, and the page fell through to its local
 * `videoViews / impressions` division over the ENTIRE campaign.
 *
 * For Never Stop Chasing that turned a governed 25.38% into 0.63%:
 *
 *   governed   28,135 / 110,866   = 25.3775%   YouTube views / YouTube impressions
 *   Optimus    28,135 / 4,474,713 =  0.6288%   same views / every impression served
 *
 * Same numerator. The denominator had been widened to include the millions of
 * CTV and Display impressions that could not report a view, so the campaign was
 * judged NEEDS ATTENTION against a rate that described no measurable thing.
 *
 * These helpers keep the canonical rate attached from the snapshot through to
 * the benchmark evaluator, and make "canonical wins" the explicit rule rather
 * than something each call site has to remember.
 */

/*
 * The rates the canonical layer owns. Optimus may CARRY these; it may not
 * invent them. Any projection that rebuilds a totals object must spread
 * pickCanonicalRates() so a newly governed rate cannot be silently dropped the
 * way videoViewRate was.
 */
export const CANONICAL_RATE_FIELDS = Object.freeze([
  "ctr",
  "cpm",
  "cpc",
  "engagementRate",
  "videoViewRate",
  "completionRate",
]);

/*
 * The COUNTS the canonical layer owns, for exactly the reason the rates above
 * are listed: a governed field that no projection whitelists is a governed
 * field that gets dropped.
 *
 * `qualifiedVideoViews` is the campaign-level video KPI — CTV completed views
 * plus YouTube TrueView views, blended by declared ruling rather than by
 * assuming two providers count the same event. Optimus was carrying the
 * governed videoViewRate (BE #218/#219) beside the raw `videoViews` the rate
 * deliberately excludes CTV from, so the card read "Video Views 31,499 / View
 * Rate 24.41%" on a flight that had delivered 196,583 qualified video
 * outcomes. Same defect shape as the rate one, one level down.
 *
 * `videoCompletions` is here because the count and the rate beside it must be
 * scoped to one platform population, and only the canonical layer knows which.
 */
export const CANONICAL_COUNT_FIELDS = Object.freeze([
  "qualifiedVideoViews",
  "videoCompletions",
]);

const finiteOrNull = (value) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/*
 * Read a canonical rate off a payload, accepting the snake_case spelling the
 * ingest surfaces still emit. Absent stays absent: a missing governed rate is
 * not zero, and it must not read as one.
 */
const readCanonicalRate = (source, field) => {
  if (!source || typeof source !== "object") return null;
  const snake = field.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
  for (const key of [field, snake]) {
    const raw = source[key];
    if (raw === null || raw === undefined || raw === "") continue;
    const numeric = finiteOrNull(Number(raw));
    if (numeric !== null) return numeric;
  }
  return null;
};

/*
 * Lift every canonical rate a payload carries. Only fields the source actually
 * resolved are returned, so spreading this can never overwrite a value a call
 * site derived with `null`.
 */
export const pickCanonicalRates = (source) => {
  const out = {};
  if (!source || typeof source !== "object") return out;
  for (const field of CANONICAL_RATE_FIELDS) {
    const value = readCanonicalRate(source, field);
    if (value !== null) out[field] = value;
  }
  /*
   * The governed counts travel with the governed rates. Keeping them in one
   * lift is deliberate: every projection on the page already spreads this, so
   * a count cannot be carried on some paths and dropped on others — which is
   * the failure mode the rate list was written to close.
   */
  for (const field of CANONICAL_COUNT_FIELDS) {
    const value = readCanonicalRate(source, field);
    if (value !== null) out[field] = value;
  }
  return out;
};

/*
 * The signal rates only — no commercial basis. Per-platform maps carry their
 * own already-priced ctr/cpm and never carry cpc, so lifting the commercial
 * fields onto them would introduce a spend-basis value that nothing reads and
 * that no call site has proven to be client-billed.
 */
export const pickCanonicalSignalRates = (source) => {
  const all = pickCanonicalRates(source);
  const out = {};
  for (const field of ["engagementRate", "videoViewRate", "completionRate"]) {
    if (all[field] !== undefined) out[field] = all[field];
  }
  return out;
};

/*
 * Canonical wins. The local division survives only for payloads that carry raw
 * counts and no governed rate at all — a snapshot shape the canonical layer
 * never touched. When neither resolves, the answer is absent, not zero.
 */
export const resolveGovernedRate = (canonicalValue, computeLocalFallback) => {
  const canonical = finiteOrNull(canonicalValue);
  if (canonical !== null) return canonical;
  if (typeof computeLocalFallback !== "function") return null;
  return finiteOrNull(computeLocalFallback());
};

/*
 * The basis Optimus is scoring, for diagnostics and for SHERLOC. A card does
 * not have to render this, but "why is Optimus using this rate?" must always
 * have an answer that names the numerator, the denominator, and who was left
 * out of both.
 */
export const describeGovernedRateBasis = ({
  metric,
  value,
  canonical,
  byPlatform = {},
  numeratorField,
  denominatorField,
  supports,
} = {}) => {
  const included = [];
  const excluded = [];
  for (const [platformKey, metrics] of Object.entries(byPlatform || {})) {
    const eligible = typeof supports === "function" ? supports(platformKey, metrics || {}) : true;
    if (eligible) included.push(platformKey);
    else excluded.push(platformKey);
  }
  return {
    metric: metric || null,
    value: finiteOrNull(value),
    basis: canonical ? "canonical" : finiteOrNull(value) === null ? "unavailable" : "local_fallback",
    numerator: numeratorField || null,
    denominator: denominatorField || null,
    included,
    excluded,
  };
};
